<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>OSPF 管理</strong>
        <small class="text-muted ml-2">instance / area / interface-template（RouterOS 7）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openInstanceModal()">新建 Instance</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CAlert v-if="!loading && routers.length === 0" color="info" fade>
          暂无 OSPF 配置。点击右上角「新建 Instance」在某台路由器上创建 OSPF 实例。
        </CAlert>

        <!-- 地域分组（内含路由器分组） -->
        <div v-for="g in regionGroups" :key="g.key" class="mb-3">
          <div class="ospf-region-header" @click="toggleRegion(g.key)">
            <strong>{{ g.name }}</strong>
            <CBadge color="dark" class="ml-2">{{ g.routers.length }} 台路由器</CBadge>
            <span class="ml-auto">
              <CButton color="light" size="sm">{{ expandedRegions.includes(g.key) ? '收起 ▲' : '展开 ▼' }}</CButton>
            </span>
          </div>
          <div v-if="expandedRegions.includes(g.key)" class="mt-2">
        <div v-for="r in g.routers" :key="r.router_id" class="mb-3">
          <div class="ospf-router-header" @click="toggleRouter(r.router_id)">
            <strong>{{ r.router_name }}</strong>
            <small class="text-muted ml-2">{{ r.router_host }}</small>
            <CBadge color="info" class="ml-2">{{ r.instances.length }} instance</CBadge>
            <span class="ml-auto">
              <CButtonGroup size="sm">
                <CButton color="info" size="sm" @click.stop="openNeighbors(r)">邻居</CButton>
                <CButton color="primary" size="sm" @click.stop="openInstanceModal(null, r)">新建 Instance</CButton>
                <CButton color="light" size="sm">{{ expandedRouters.includes(r.router_id) ? '收起 ▲' : '展开 ▼' }}</CButton>
              </CButtonGroup>
            </span>
          </div>

          <div v-if="expandedRouters.includes(r.router_id)" class="ospf-router-body">
            <div v-for="ins in r.instances" :key="ins.id" class="ospf-instance">
              <div class="ospf-instance-header">
                <strong>{{ ins.name }}</strong>
                <CBadge :color="ins.version === 3 ? 'dark' : 'secondary'" class="ml-2">
                  OSPFv{{ ins.version }}
                </CBadge>
                <CBadge :color="ins.enabled ? 'success' : 'secondary'" class="ml-1">
                  {{ ins.enabled ? '启用' : '禁用' }}
                </CBadge>
                <span class="ml-auto">
                  <CButtonGroup size="sm">
                    <CButton color="success" size="sm" @click="openAreaModal(ins)">新建 Area</CButton>
                    <CButton color="info" size="sm" @click="openInstanceModal(ins, r)">编辑</CButton>
                    <CButton color="dark" size="sm" @click="openCopyModal(ins, r)">复制</CButton>
                    <CButton color="warning" size="sm" @click="toggleInstance(ins)">{{ ins.enabled ? '禁用' : '启用' }}</CButton>
                    <CButton color="danger" size="sm" @click="deleteInstance(ins, r)">删除</CButton>
                  </CButtonGroup>
                </span>
              </div>
              <div class="ospf-instance-meta text-muted">
                <span v-if="ins.router_id_config">router-id: <code>{{ ins.router_id_config }}</code></span>
                <span class="ml-3">vrf: <code>{{ ins.vrf || 'main' }}</code></span>
                <span v-if="ins.redistribute" class="ml-3">redistribute: <code>{{ ins.redistribute }}</code></span>
                <span v-if="ins.originate_default" class="ml-3">originate-default: <code>{{ ins.originate_default }}</code></span>
                <span v-if="ins.in_filter_chain" class="ml-3">in-filter: <code>{{ ins.in_filter_chain }}</code></span>
                <span v-if="ins.out_filter_chain" class="ml-3">out-filter: <code>{{ ins.out_filter_chain }}</code></span>
                <span v-if="ins.out_filter_select" class="ml-3">out-select: <code>{{ ins.out_filter_select }}</code></span>
              </div>

              <!-- Areas -->
              <div v-for="area in ins.areas" :key="area.id" class="ospf-area">
                <div class="ospf-area-header">
                  <strong>{{ area.name }}</strong>
                  <small class="text-muted ml-2">area-id {{ area.area_id }}</small>
                  <CBadge color="light" class="ml-2">{{ area.type }}</CBadge>
                  <CBadge v-if="area.type === 'stub' && area.no_summaries" color="dark" class="ml-1">totally-stub</CBadge>
                  <CBadge :color="area.enabled ? 'success' : 'secondary'" class="ml-1">
                    {{ area.enabled ? '启用' : '禁用' }}
                  </CBadge>
                  <span class="ml-auto">
                    <CButtonGroup size="sm">
                      <CButton color="success" size="sm" @click="openTemplateModal(area, ins)">新建 Template</CButton>
                      <CButton color="info" size="sm" @click="openAreaModal(ins, area)">编辑</CButton>
                      <CButton color="warning" size="sm" @click="toggleArea(area)">{{ area.enabled ? '禁用' : '启用' }}</CButton>
                      <CButton color="danger" size="sm" @click="deleteArea(area, ins)">删除</CButton>
                    </CButtonGroup>
                  </span>
                </div>

                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>networks / interfaces</th>
                      <th width="110">type</th>
                      <th width="80">passive</th>
                      <th width="70">cost</th>
                      <th width="210">timers (hello/dead/retx/txd)</th>
                      <th width="70">prio</th>
                      <th width="90">auth</th>
                      <th width="200">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in area.templates" :key="t.id" :class="{ 'text-muted': !t.enabled }">
                      <td>
                        <div v-if="t.networks"><code>{{ t.networks }}</code></div>
                        <div v-if="t.interfaces"><small>iface: {{ t.interfaces }}</small></div>
                      </td>
                      <td>{{ t.type }}</td>
                      <td>{{ t.passive ? '✓' : '' }}</td>
                      <td>{{ t.cost }}</td>
                      <td><small>{{ t.hello_interval }} / {{ t.dead_interval }} / {{ t.retransmit_interval }} / {{ t.transmit_delay }}</small></td>
                      <td>{{ t.priority }}</td>
                      <td>{{ t.auth || '-' }}</td>
                      <td>
                        <CButtonGroup size="sm">
                          <CButton color="info" size="sm" @click="openTemplateModal(area, ins, t)">编辑</CButton>
                          <CButton color="warning" size="sm" @click="toggleTemplate(t)">{{ t.enabled ? '禁用' : '启用' }}</CButton>
                          <CButton color="danger" size="sm" @click="deleteTemplate(t, area)">删除</CButton>
                        </CButtonGroup>
                      </td>
                    </tr>
                    <tr v-if="!area.templates || area.templates.length === 0">
                      <td colspan="8" class="text-center text-muted">暂无 interface-template</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!ins.areas || ins.areas.length === 0" class="text-muted ml-3 mb-2">
                暂无 area
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Instance Modal -->
    <CModal :show.sync="instanceModal.show" :title="instanceModal.isEdit ? '编辑 OSPF Instance' : '新建 OSPF Instance'" color="primary" size="lg">
      <CForm>
        <CSelect
          v-if="!instanceModal.isEdit"
          label="路由器"
          v-model="instanceModal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
          @change="onOspfRouterChange"
        />
        <CInput label="Instance 名称" v-model="instanceModal.form.name" placeholder="如 ospf-instance-sz-core-bbr01-ipv4" />
        <CRow>
          <CCol sm="4">
            <CSelect label="版本" v-model="instanceModal.form.version" :options="[
              { value: 2, label: 'OSPFv2 (IPv4)' },
              { value: 3, label: 'OSPFv3 (IPv6)' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Router ID（从 IP 管理选择，必填）" v-model="instanceModal.form.router_id_config" :options="routerIdOptions" placeholder="请选择" />
          </CCol>
          <CCol sm="4">
            <CInput label="VRF" v-model="instanceModal.form.vrf" placeholder="main" />
          </CCol>
        </CRow>
        <div class="form-group">
          <label>Redistribute</label>
          <div>
            <CInputCheckbox
              v-for="rd in redistributeTypes"
              :key="rd"
              :label="rd"
              :checked="instanceModal.form.redistribute.includes(rd)"
              @update:checked="v => toggleArrayItem(instanceModal.form.redistribute, rd, v)"
              inline
            />
          </div>
        </div>
        <CRow>
          <CCol sm="4">
            <CSelect label="Originate Default" v-model="instanceModal.form.originate_default" :options="[
              { value: '', label: '（不设置）' },
              { value: 'never', label: 'never' },
              { value: 'if-installed', label: 'if-installed' },
              { value: 'always', label: 'always' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="In Filter Chain" v-model="instanceModal.form.in_filter_chain" :options="chainSelectOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Out Filter Chain" v-model="instanceModal.form.out_filter_chain" :options="chainSelectOptions" placeholder="（不设置）" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="4">
            <CSelect label="Out Filter Select" v-model="instanceModal.form.out_filter_select" :options="chainSelectOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CInput label="MPLS TE Area" v-model="instanceModal.form.mpls_te_area" placeholder="（可选）" />
          </CCol>
          <CCol sm="4">
            <CInput label="Domain ID" v-model="instanceModal.form.domain_id" placeholder="（可选）" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="6">
            <CInput label="Domain Tag" v-model="instanceModal.form.domain_tag" placeholder="整数 [0..4294967295]（可选）" />
          </CCol>
          <CCol sm="6">
            <CSelect label="Use DN" v-model="instanceModal.form.use_dn" :options="[
              { value: '', label: '（不设置）' },
              { value: 'yes', label: 'yes' },
              { value: 'no', label: 'no' }
            ]" />
          </CCol>
        </CRow>
        <CAlert color="info" fade>
          Filter Chain 只能选择<b>已下发到该路由器</b>的 chain（下拉随所选路由器实时加载）。未下发的请先到 Router Filters 页面同步。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="instanceModal.show = false">取消</CButton>
          <CButton color="success" :disabled="instanceModal.loading" @click="submitInstance">
            {{ instanceModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 复制 Instance Modal -->
    <CModal :show.sync="copyModal.show" :title="'复制 OSPF 配置 - ' + (copyModal.src ? copyModal.src.name : '')" color="dark" size="lg">
      <CForm>
        <CAlert color="info" fade>
          整树复制：instance + 全部 areas + 全部 interface-templates 一次性下发到目标路由器。
          目标路由器上已有同名 instance 或 area 时会被拒绝（先改名或删除冲突项）。
        </CAlert>
        <div class="form-group">
          <label class="form-label">目标路由器</label>
          <SearchSelect :options="routerOptions" v-model="copyModal.target_router_id" placeholder="输入路由器名筛选" @change="onCopyTargetChange" />
        </div>
        <CInput label="Instance 名称（默认与源相同）" v-model="copyModal.name" placeholder="如 ospf-instance-xxx-ipv4" />
        <div class="form-group">
          <label class="form-label">Router ID（跨路由器复制请使用目标路由器的 router-id）</label>
          <SearchSelect :options="copyRouterIdOptions" v-model="copyModal.router_id_config" placeholder="输入选择或留空沿用源值" />
        </div>
        <CAlert v-if="copyModal.areaCount > 0" color="secondary" fade class="mb-0">
          将复制 {{ copyModal.areaCount }} 个 area（含 {{ copyModal.tplCount }} 个 template）。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="copyModal.show = false">取消</CButton>
          <CButton color="success" :disabled="copyModal.loading" @click="submitCopy">{{ copyModal.loading ? '复制中...' : '复制' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Area Modal -->
    <CModal :show.sync="areaModal.show" :title="areaModal.isEdit ? '编辑 OSPF Area' : '新建 OSPF Area'" color="primary">
      <CForm>
        <CInput v-if="!areaModal.isEdit" label="所属 Instance" :value="areaModal.instanceName" disabled />
        <CInput label="Area 名称" v-model="areaModal.form.name" placeholder="如 ospf-area-sz-backbone" />
        <CInput label="Area ID" v-model="areaModal.form.area_id" placeholder="0.0.0.0（backbone）" />
        <CRow>
          <CCol sm="6">
            <CSelect label="类型" v-model="areaModal.form.type" :options="[
              { value: 'default', label: 'default（标准区）' },
              { value: 'stub', label: 'stub（末梢区）' },
              { value: 'nssa', label: 'nssa（NSSA区）' }
            ]" />
          </CCol>
          <CCol sm="6" v-if="areaModal.form.type === 'stub' || areaModal.form.type === 'nssa'">
            <CSelect label="No Summaries（完全末梢）" v-model="areaModal.form.no_summaries" :options="[
              { value: 0, label: 'no' },
              { value: 1, label: 'yes' }
            ]" />
          </CCol>
          <CCol sm="6" v-if="areaModal.form.type === 'nssa'">
            <CSelect label="NSSA Translate" v-model="areaModal.form.nssa_translate" :options="[
              { value: 'candidate', label: 'candidate' },
              { value: 'yes', label: 'yes' },
              { value: 'no', label: 'no' }
            ]" />
          </CCol>
        </CRow>
        <CInput
          v-if="areaModal.form.type === 'stub' || areaModal.form.type === 'nssa'"
          label="Default Cost"
          v-model="areaModal.form.default_cost"
          placeholder="注入 LSA 的默认 cost（可选）"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="areaModal.show = false">取消</CButton>
          <CButton color="success" :disabled="areaModal.loading" @click="submitArea">
            {{ areaModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Interface Template Modal -->
    <CModal :show.sync="templateModal.show" :title="templateModal.isEdit ? '编辑 Interface Template' : '新建 Interface Template'" color="primary" size="lg">
      <CForm>
        <CInput v-if="!templateModal.isEdit" label="所属 Area" :value="templateModal.areaName" disabled />
        <CRow>
          <CCol sm="6">
            <CInput label="Networks（网段，逗号分隔）" v-model="templateModal.form.networks" placeholder="如 33.100.100.148/30" />
          </CCol>
          <CCol sm="6">
            <CInput label="Interfaces（接口/接口列表名，可选）" v-model="templateModal.form.interfaces" placeholder="如 ether1 或 ALL" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="4">
            <CSelect label="网络类型" v-model="templateModal.form.type" :options="[
              { value: 'broadcast', label: 'broadcast' },
              { value: 'ptp', label: 'ptp' },
              { value: 'ptmp', label: 'ptmp' },
              { value: 'nbma', label: 'nbma' },
              { value: 'ptp-unnumbered', label: 'ptp-unnumbered' },
              { value: 'virtual-link', label: 'virtual-link' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Passive" v-model="templateModal.form.passive" :options="[
              { value: 0, label: 'no' },
              { value: 1, label: 'yes（不收发 OSPF 报文）' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CInput label="Cost" v-model="templateModal.form.cost" placeholder="10（0-65535）" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="3">
            <CInput label="Hello Interval" v-model="templateModal.form.hello_interval" placeholder="10s" />
          </CCol>
          <CCol sm="3">
            <CInput label="Dead Interval" v-model="templateModal.form.dead_interval" placeholder="40s" />
          </CCol>
          <CCol sm="3">
            <CInput label="Retransmit Interval" v-model="templateModal.form.retransmit_interval" placeholder="5s" />
          </CCol>
          <CCol sm="3">
            <CInput label="Transmit Delay" v-model="templateModal.form.transmit_delay" placeholder="1s" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="4">
            <CInput label="Priority" v-model="templateModal.form.priority" placeholder="128（0-255）" />
          </CCol>
          <CCol sm="4">
            <CInput label="Instance ID (v3)" v-model="templateModal.form.ospf_instance_id" placeholder="0（0-255）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="认证方式" v-model="templateModal.form.auth" :options="[
              { value: '', label: '（无）' },
              { value: 'simple', label: 'simple' },
              { value: 'md5', label: 'md5' },
              { value: 'sha1', label: 'sha1' },
              { value: 'sha256', label: 'sha256' },
              { value: 'sha384', label: 'sha384' },
              { value: 'sha512', label: 'sha512' }
            ]" />
          </CCol>
        </CRow>
        <CRow v-if="templateModal.form.auth && templateModal.form.auth !== ''">
          <CCol sm="4">
            <CInput label="Auth ID" v-model="templateModal.form.auth_id" placeholder="整数 key id" />
          </CCol>
          <CCol sm="8">
            <CInput label="Auth Key" v-model="templateModal.form.auth_key" placeholder="认证密钥（同网段所有邻居须一致）" />
          </CCol>
        </CRow>
        <CRow v-if="templateModal.form.type === 'virtual-link'">
          <CCol sm="6">
            <CInput label="Vlink Neighbor ID" v-model="templateModal.form.vlink_neighbor_id" placeholder="对端 router-id，如 1.1.1.1" />
          </CCol>
          <CCol sm="6">
            <CInput label="Vlink Transit Area" v-model="templateModal.form.vlink_transit_area" placeholder="穿越的 transit area 名称" />
          </CCol>
        </CRow>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="templateModal.show = false">取消</CButton>
          <CButton color="success" :disabled="templateModal.loading" @click="submitTemplate">
            {{ templateModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Neighbors Modal -->
    <CModal :show.sync="neighborsModal.show" :title="'OSPF 邻居 - ' + neighborsModal.routerName" color="dark" size="xl">
      <div v-if="neighborsModal.loading" class="text-center text-muted my-3">拉取中...</div>
      <table v-else class="table table-sm">
        <thead>
          <tr>
            <th>Instance</th>
            <th>Router ID</th>
            <th>Address</th>
            <th>Interface</th>
            <th>State</th>
            <th>Adjacency</th>
            <th>Priority</th>
            <th>DR / BDR</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(n, i) in neighborsModal.list" :key="i" :class="{ 'text-muted': n.inactive }">
            <td>{{ n.instance }}</td>
            <td><code>{{ n.router_id }}</code></td>
            <td>{{ n.address }}</td>
            <td>{{ n.interface }}</td>
            <td>
              <CBadge :color="stateColor(n.state)">{{ n.state }}</CBadge>
            </td>
            <td>{{ n.adjacency }}</td>
            <td>{{ n.priority }}</td>
            <td><small>{{ n.dr }} / {{ n.bdr }}</small></td>
          </tr>
          <tr v-if="neighborsModal.list.length === 0">
            <td colspan="8" class="text-center text-muted">无邻居</td>
          </tr>
        </tbody>
      </table>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="openNeighbors(neighborsModal.routerRef)">刷新</CButton>
          <CButton color="danger" @click="neighborsModal.show = false">关闭</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
import SearchSelect from '@/components/SearchSelect'

export default {
  name: 'OspfList',
  components: { SearchSelect },
  data() {
    return {
      loading: false,
      routers: [],
      allRouters: [],
      regions: [],
      expandedRegions: [],
      chainNames: [],
      routerChainNames: [],
      routerIps: [],
      expandedRouters: [],
      redistributeTypes: ['connected', 'static', 'bgp', 'rip', 'ospf', 'vpn', 'dhcp', 'modem', 'fantasy', 'copy'],
      instanceModal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        routerRef: null,
        form: this.emptyInstanceForm()
      },
      areaModal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        instanceId: null,
        instanceName: '',
        form: this.emptyAreaForm()
      },
      copyModal: {
        show: false,
        loading: false,
        src: null,
        target_router_id: '',
        name: '',
        router_id_config: '',
        areaCount: 0,
        tplCount: 0
      },
      templateModal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        areaId: null,
        areaName: '',
        form: this.emptyTemplateForm()
      },
      neighborsModal: {
        show: false,
        loading: false,
        routerName: '',
        routerRef: null,
        list: []
      },
      confirmModal: {
        show: false,
        title: '确认',
        message: '',
        callback: () => {}
      }
    }
  },
  computed: {
    routerOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: `${r.name} (${r.host})` }))
    },
    regionGroups() {
      // 按地域归类路由器（allRouters 提供 region_id, regions 提供名称）; 无地域 → 「未设置地域」排最后
      const map = {}
      this.regions.forEach(g => { map[g.id] = g })
      const groups = {}
      this.routers.forEach(r => {
        const rt = this.allRouters.find(a => a.id === r.router_id)
        const rid = (rt && rt.region_id !== null && rt.region_id !== undefined) ? rt.region_id : 'none'
        if (!groups[rid]) {
          groups[rid] = {
            key: rid,
            name: map[rid] ? map[rid].name : '未设置地域',
            order: rid === 'none' ? 99999 : rid,
            routers: []
          }
        }
        groups[rid].routers.push(r)
      })
      return Object.values(groups).sort((a, b) => a.order - b.order)
    },
    copyRouterIdOptions() {
      // 目标路由器上已纳管的 IPv4 地址（作为 router-id 候选）
      const rid = this.copyModal.target_router_id
      if (!rid) return []
      return this.routerIps
        .filter(i => i.family === 'ipv4' && i.router_id == rid)
        .map(i => ({ value: i.address.split('/')[0], label: `${i.address} @ ${i.interface_name}` }))
    },
    chainSelectOptions() {
      const opts = [{ value: '', label: '（不设置）' }]
      // 只展示该路由器上实际已下发的 chain（实时查询路由器 /routing/filter/rule + /routing/filterchain）
      // 注意: 此 computed 不能依赖 instanceModal.form 的值, 否则 CSelect v-model 的事件时序会导致 [object Event]
      return opts.concat(this.routerChainNames.map(n => ({ value: n, label: n })))
    },
    routerIdOptions() {
      const opts = []
      this.routerIps.filter(i => i.family === 'ipv4').forEach(i => {
        const ip = (i.address || '').split('/')[0]
        if (ip) opts.push({ value: ip, label: `${ip} (${i.interface_name})` })
      })
      return opts
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
    this.loadChains()
    this.loadRegions()
  },
  methods: {
    emptyInstanceForm() {
      return {
        router_id: '', name: '', version: 2, router_id_config: '', vrf: 'main',
        redistribute: [], originate_default: '', in_filter_chain: '',
        out_filter_chain: '', out_filter_select: '', mpls_te_area: '',
        domain_id: '', domain_tag: '', use_dn: ''
      }
    },
    emptyAreaForm() {
      return { name: '', area_id: '0.0.0.0', type: 'default', no_summaries: 0, default_cost: '', nssa_translate: 'candidate' }
    },
    emptyTemplateForm() {
      return {
        interfaces: '', networks: '', type: 'broadcast', passive: 0, cost: '10',
        hello_interval: '10s', dead_interval: '40s', retransmit_interval: '5s',
        transmit_delay: '1s', priority: '128', ospf_instance_id: '0',
        auth: '', auth_id: '', auth_key: '', vlink_neighbor_id: '', vlink_transit_area: ''
      }
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadChains() {
      try {
        const res = await this.$axios.get('user/routing/filter/chains')
        if (res.data.code === 200) {
          this.chainNames = (res.data.data || []).map(c => c.name)
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/ospf/list')
        if (res.data.code === 200) {
          this.routers = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    async loadRegions() {
      try {
        const res = await this.$axios.get('user/region/list')
        if (res.data.code === 200) {
          this.regions = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    toggleRegion(key) {
      const i = this.expandedRegions.indexOf(key)
      if (i >= 0) this.expandedRegions.splice(i, 1)
      else this.expandedRegions.push(key)
    },
    toggleRouter(id) {
      const i = this.expandedRouters.indexOf(id)
      if (i >= 0) this.expandedRouters.splice(i, 1)
      else this.expandedRouters.push(id)
    },
    async loadRouterIps(routerId) {
      this.routerIps = []
      if (!routerId) return
      try {
        const res = await this.$axios.get('user/ip-address/list', { params: { router_id: routerId } })
        if (res.data.code === 200) {
          this.routerIps = res.data.ip_addresses || []
        }
      } catch (e) { /* ignore */ }
    },
    onOspfRouterChange() {
      this.loadRouterIps(this.instanceModal.form.router_id)
      this.loadRouterChains(this.instanceModal.form.router_id)
    },
    openCopyModal(ins) {
      this.copyModal.src = ins
      this.copyModal.target_router_id = ins.router_id || ''
      this.copyModal.name = ins.name
      this.copyModal.router_id_config = ''
      const areas = (ins.areas || [])
      this.copyModal.areaCount = areas.length
      this.copyModal.tplCount = areas.reduce((s, a) => s + ((a.templates || []).length), 0)
      this.copyModal.show = true
      this.loadRouterIps(this.copyModal.target_router_id)
    },
    onCopyTargetChange() {
      this.copyModal.router_id_config = ''
      this.loadRouterIps(this.copyModal.target_router_id)
    },
    async submitCopy() {
      const ins = this.copyModal.src
      if (!ins) return
      if (!this.copyModal.target_router_id) {
        this.$toasted.show('请选择目标路由器', { type: 'error' })
        return
      }
      this.copyModal.loading = true
      try {
        const res = await this.$axios.post(`user/routing/ospf/instance/${ins.id}/copy`, {
          target_router_id: this.copyModal.target_router_id,
          name: this.copyModal.name,
          router_id_config: this.copyModal.router_id_config
        })
        if (res.data.code === 200) {
          this.$toasted.show(res.data.msg || '已复制', { type: 'success' })
          this.copyModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '复制失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.copyModal.loading = false
    },
    async loadRouterChains(routerId) {
      this.routerChainNames = []
      if (!routerId) return
      try {
        const res = await this.$axios.get(`user/routing/filter/routers/${routerId}/chains`)
        if (res.data.code === 200) {
          this.routerChainNames = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    toggleArrayItem(arr, item, checked) {
      const i = arr.indexOf(item)
      if (checked && i < 0) arr.push(item)
      if (!checked && i >= 0) arr.splice(i, 1)
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },

    /* Instance */
    openInstanceModal(ins, routerRef) {
      if (ins) {
        this.loadRouterIps(ins.router_id)
        this.loadRouterChains(ins.router_id)
        this.instanceModal.isEdit = true
        this.instanceModal.editId = ins.id
        this.instanceModal.routerRef = routerRef
        this.instanceModal.form = {
          router_id: ins.router_id,
          name: ins.name,
          version: ins.version,
          router_id_config: ins.router_id_config || '',
          vrf: ins.vrf || 'main',
          redistribute: ins.redistribute ? ins.redistribute.split(',') : [],
          originate_default: ins.originate_default || '',
          in_filter_chain: ins.in_filter_chain || '',
          out_filter_chain: ins.out_filter_chain || '',
          out_filter_select: ins.out_filter_select || '',
          mpls_te_area: ins.mpls_te_area || '',
          domain_id: ins.domain_id || '',
          domain_tag: ins.domain_tag !== null && ins.domain_tag !== undefined ? String(ins.domain_tag) : '',
          use_dn: ins.use_dn || ''
        }
      } else {
        this.instanceModal.isEdit = false
        this.instanceModal.editId = null
        this.instanceModal.routerRef = routerRef
        this.instanceModal.form = this.emptyInstanceForm()
        if (routerRef) {
          this.instanceModal.form.router_id = routerRef.router_id
        }
        this.loadRouterIps(this.instanceModal.form.router_id)
        this.loadRouterChains(this.instanceModal.form.router_id)
      }
      this.instanceModal.show = true
    },
    async submitInstance() {
      const f = this.instanceModal.form
      if (!this.instanceModal.isEdit && !f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      if (!f.name) {
        this.$toasted.show('请填写 Instance 名称', { type: 'error' })
        return
      }
      if (!f.router_id_config) {
        this.$toasted.show('请选择 Router ID', { type: 'error' })
        return
      }
      this.instanceModal.loading = true
      try {
        let res
        if (this.instanceModal.isEdit) {
          res = await this.$axios.post(`user/ospf/instance/${this.instanceModal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/ospf/instance/add', f)
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.instanceModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.instanceModal.loading = false
    },
    toggleInstance(ins) {
      this.showConfirm('确认操作', `确认${ins.enabled ? '禁用' : '启用'} instance "${ins.name}"？`, async () => {
        try {
          const res = await this.$axios.post(`user/ospf/instance/${ins.id}/toggle`)
          if (res.data.code === 200) {
            this.$toasted.show('已切换', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteInstance(ins, routerRef) {
      const areaCount = (ins.areas || []).length
      const tplCount = (ins.areas || []).reduce((s, a) => s + (a.templates || []).length, 0)
      this.showConfirm(
        '确认删除',
        `确认删除 instance "${ins.name}"？${areaCount > 0 ? `将级联删除 ${areaCount} 个 area${tplCount > 0 ? ` 和 ${tplCount} 个 interface-template` : ''}。` : ''}路由器上的对应配置会一并移除。`,
        async () => {
          try {
            const res = await this.$axios.delete(`user/ospf/instance/${ins.id}/delete`)
            if (res.data.code === 200) {
              this.$toasted.show('已删除', { type: 'success' })
              this.loadList()
            } else {
              this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
            }
          } catch (e) {
            this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
          }
        }
      )
    },

    /* Area */
    openAreaModal(ins, area) {
      this.areaModal.instanceId = ins.id
      this.areaModal.instanceName = ins.name
      if (area) {
        this.areaModal.isEdit = true
        this.areaModal.editId = area.id
        this.areaModal.form = {
          name: area.name,
          area_id: area.area_id,
          type: area.type,
          no_summaries: area.no_summaries ? 1 : 0,
          default_cost: area.default_cost !== null && area.default_cost !== undefined ? String(area.default_cost) : '',
          nssa_translate: area.nssa_translate || 'candidate'
        }
      } else {
        this.areaModal.isEdit = false
        this.areaModal.editId = null
        this.areaModal.form = this.emptyAreaForm()
      }
      this.areaModal.show = true
    },
    async submitArea() {
      const f = this.areaModal.form
      if (!f.name) {
        this.$toasted.show('请填写 Area 名称', { type: 'error' })
        return
      }
      this.areaModal.loading = true
      try {
        let res
        if (this.areaModal.isEdit) {
          res = await this.$axios.post(`user/ospf/area/${this.areaModal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/ospf/area/add', Object.assign({ instance_id: this.areaModal.instanceId }, f))
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.areaModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.areaModal.loading = false
    },
    toggleArea(area) {
      this.showConfirm('确认操作', `确认${area.enabled ? '禁用' : '启用'} area "${area.name}"？`, async () => {
        try {
          const res = await this.$axios.post(`user/ospf/area/${area.id}/toggle`)
          if (res.data.code === 200) {
            this.$toasted.show('已切换', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteArea(area, ins) {
      const tplCount = (area.templates || []).length
      this.showConfirm(
        '确认删除',
        `确认删除 area "${area.name}"${ins ? `（instance ${ins.name}）` : ''}？${tplCount > 0 ? `将级联删除 ${tplCount} 个 interface-template。` : ''}`,
        async () => {
          try {
            const res = await this.$axios.delete(`user/ospf/area/${area.id}/delete`)
            if (res.data.code === 200) {
              this.$toasted.show('已删除', { type: 'success' })
              this.loadList()
            } else {
              this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
            }
          } catch (e) {
            this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
          }
        }
      )
    },

    /* Interface Template */
    openTemplateModal(area, ins, tpl) {
      this.templateModal.areaId = area.id
      this.templateModal.areaName = area.name
      if (tpl) {
        this.templateModal.isEdit = true
        this.templateModal.editId = tpl.id
        this.templateModal.form = {
          interfaces: tpl.interfaces || '',
          networks: tpl.networks || '',
          type: tpl.type,
          passive: tpl.passive ? 1 : 0,
          cost: tpl.cost !== null && tpl.cost !== undefined ? String(tpl.cost) : '',
          hello_interval: tpl.hello_interval || '10s',
          dead_interval: tpl.dead_interval || '40s',
          retransmit_interval: tpl.retransmit_interval || '5s',
          transmit_delay: tpl.transmit_delay || '1s',
          priority: tpl.priority !== null && tpl.priority !== undefined ? String(tpl.priority) : '128',
          ospf_instance_id: tpl.ospf_instance_id !== null && tpl.ospf_instance_id !== undefined ? String(tpl.ospf_instance_id) : '0',
          auth: tpl.auth || '',
          auth_id: tpl.auth_id !== null && tpl.auth_id !== undefined ? String(tpl.auth_id) : '',
          auth_key: '',
          vlink_neighbor_id: tpl.vlink_neighbor_id || '',
          vlink_transit_area: tpl.vlink_transit_area || ''
        }
      } else {
        this.templateModal.isEdit = false
        this.templateModal.editId = null
        this.templateModal.form = this.emptyTemplateForm()
      }
      this.templateModal.show = true
    },
    async submitTemplate() {
      const f = this.templateModal.form
      if (!f.networks && !f.interfaces) {
        this.$toasted.show('networks 或 interfaces 至少填一项', { type: 'error' })
        return
      }
      if (f.auth && !f.auth_key && !this.templateModal.isEdit) {
        this.$toasted.show('选择认证方式后必须填写 Auth Key', { type: 'error' })
        return
      }
      this.templateModal.loading = true
      try {
        let res
        if (this.templateModal.isEdit) {
          res = await this.$axios.post(`user/ospf/interface-template/${this.templateModal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/ospf/interface-template/add', Object.assign({ area_id: this.templateModal.areaId }, f))
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.templateModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.templateModal.loading = false
    },
    toggleTemplate(t) {
      this.showConfirm('确认操作', `确认${t.enabled ? '禁用' : '启用'}该 interface-template（networks: ${t.networks || t.interfaces}）？`, async () => {
        try {
          const res = await this.$axios.post(`user/ospf/interface-template/${t.id}/toggle`)
          if (res.data.code === 200) {
            this.$toasted.show('已切换', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteTemplate(t, area) {
      this.showConfirm(
        '确认删除',
        `确认删除 interface-template（networks: ${t.networks || t.interfaces}${area ? `，area ${area.name}` : ''}）？`,
        async () => {
          try {
            const res = await this.$axios.delete(`user/ospf/interface-template/${t.id}/delete`)
            if (res.data.code === 200) {
              this.$toasted.show('已删除', { type: 'success' })
              this.loadList()
            } else {
              this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
            }
          } catch (e) {
            this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
          }
        }
      )
    },

    /* Neighbors */
    async openNeighbors(routerRef) {
      this.neighborsModal.routerRef = routerRef
      this.neighborsModal.routerName = routerRef.router_name
      this.neighborsModal.show = true
      this.neighborsModal.loading = true
      this.neighborsModal.list = []
      try {
        const res = await this.$axios.get(`user/ospf/router/${routerRef.router_id}/neighbors`)
        if (res.data.code === 200) {
          this.neighborsModal.list = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '拉取失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.neighborsModal.loading = false
    },
    stateColor(state) {
      if (state === 'Full') return 'success'
      if (state === 'Down') return 'danger'
      if (state === 'Attempt' || state === 'Init' || state === '2-Way') return 'secondary'
      return 'warning'
    }
  }
}
</script>

<style scoped>
.ospf-region-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: rgba(69, 85, 100, 0.12);
  border-left: 4px solid #45556e;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}
.ospf-router-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}
.ospf-router-body {
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 10px;
}
.ospf-instance {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 10px;
}
.ospf-instance-header {
  display: flex;
  align-items: center;
}
.ospf-instance-meta {
  font-size: 12px;
  margin: 4px 0 8px;
}
.ospf-area {
  border: 1px dashed #ced4da;
  border-radius: 4px;
  padding: 6px 8px;
  margin: 8px 0;
}
.ospf-area-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.ml-auto {
  margin-left: auto;
}
</style>
